import { useEffect, useState } from "react";
import ApiUserUploads from "../../api/user-uploads";

// Rich text stores the upload's id next to a signed URL that lives five minutes,
// so whatever shows the text has to ask for fresh URLs first. The article modal
// grew this logic first; task descriptions (LF360-1852) use this copy of it, and
// anything else that renders editor content can import it instead of writing a third.
const collectUploadIds = (doc, content) => {
  const uploadIds = new Set();

  doc.querySelectorAll("img").forEach((img) => {
    const id = img.getAttribute("data-id");
    if (id) { uploadIds.add(id); }

    const alt = img.getAttribute("alt") || "";
    const match = alt.match(/^image-([a-f0-9-]+)$/i);
    if (match) { uploadIds.add(match[1]); }
  });

  doc.querySelectorAll("video[data-upload-id]").forEach((video) => {
    const id = video.getAttribute("data-upload-id");
    if (id) { uploadIds.add(id); }
  });

  doc.querySelectorAll("video[data-id]").forEach((video) => {
    const id = video.getAttribute("data-id");
    if (id) { uploadIds.add(id); }
  });

  const markdownImage = /!\[(image-[a-f0-9-]+)\]\((.*?)\)/gi;
  let match;
  while ((match = markdownImage.exec(content)) !== null) {
    uploadIds.add(match[1].replace("image-", ""));
  }

  return uploadIds;
};

export const refreshSignedMedia = async (content) => {
  if (!content || typeof content !== "string") { return content; }
  if (!content.includes("<img") && !content.includes("<video")) { return content; }

  const doc = new DOMParser().parseFromString(content, "text/html");
  const uploadIds = collectUploadIds(doc, content);
  if (uploadIds.size === 0) { return content; }

  let urlMap = {};
  try {
    const response = await ApiUserUploads.getSignedUrls({ imageIds: [...uploadIds] });
    urlMap = response?.data || {};
  } catch (e) {
    // Nothing to gain from replacing good URLs with nothing: keep what we have.
    console.error("Failed to refresh signed media URLs", e);
    return content;
  }

  const applyTo = (nodes, idOf) => {
    nodes.forEach((node) => {
      const id = idOf(node);
      const url = id ? urlMap[id] : null;
      if (url) { node.setAttribute("src", url); }
    });
  };

  applyTo(doc.querySelectorAll("img"), (img) => {
    const dataId = img.getAttribute("data-id");
    if (dataId) { return dataId; }
    const match = (img.getAttribute("alt") || "").match(/^image-([a-f0-9-]+)$/i);
    return match ? match[1] : null;
  });

  applyTo(doc.querySelectorAll("video"), (video) =>
    video.getAttribute("data-upload-id") || video.getAttribute("data-id"));

  return doc.body.innerHTML;
};

// Convenience for read-only surfaces: hand it stored content, render what it gives
// back. Keeps every viewer of rich text from writing the same effect (LF360-1852).
// `enabled` says whether this surface is being looked at. Turning it back on asks
// again for content that has not changed — a drawer reopening after the five minutes
// have passed, whose URLs are dead by then and whose staleness nothing else notices.
//
// The refreshed text is kept next to the content it was made from, so a drawer
// switching to another task never shows the previous one's description while the
// new one is still being signed.
export const useSignedHtml = (content, enabled = true) => {
  const [signed, setSigned] = useState({ source: content || "", html: content || "" });

  useEffect(() => {
    // nothing to sign for a surface nobody is looking at; what is already there is
    // kept, and the next open asks again
    if (enabled === false) { return undefined; }

    const source = content || "";
    let cancelled = false;

    // until the fresh URLs arrive, show this content — never the last one's
    setSigned({ source, html: source });

    (async () => {
      const refreshed = await refreshSignedMedia(source);
      if (!cancelled) { setSigned({ source, html: refreshed || "" }); }
    })();

    return () => { cancelled = true; };
  }, [content, enabled]);

  return signed.source === (content || "") ? signed.html : (content || "");
};

export default refreshSignedMedia;
